import { useState, useEffect } from 'react';

/** GeometryChangeEvent: WICG spec - titlebarAreaRect and visible from event */
type GeometryChangeEvent = Event & { titlebarAreaRect?: DOMRect; visible?: boolean };

declare global {
  interface Navigator {
    windowControlsOverlay?: {
      visible: boolean;
      getTitlebarAreaRect?: () => DOMRect;
      addEventListener?: (type: string, handler: (e: GeometryChangeEvent) => void) => void;
      removeEventListener?: (type: string, handler: (e: GeometryChangeEvent) => void) => void;
    };
  }
}

interface WindowControlsOverlayState {
  isVisible: boolean;
  titlebarAreaRect: DOMRect | null;
}

const DEBOUNCE_MS = 200;

/** Debounce that passes event to handler (Microsoft/WICG pattern) */
function debounce(
  func: (e: GeometryChangeEvent) => void,
  wait: number
): ((e: GeometryChangeEvent) => void) & { cancel: () => void } {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  const executedFunction = (e: GeometryChangeEvent) => {
    const captured = e;
    const later = () => {
      timeout = null;
      func(captured);
    };
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
  executedFunction.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };
  return executedFunction;
}

export function useWindowControlsOverlay(): WindowControlsOverlayState {
  const [state, setState] = useState<WindowControlsOverlayState>({
    isVisible: false,
    titlebarAreaRect: null,
  });

  useEffect(() => {
    const overlay = navigator.windowControlsOverlay;
    const displayModeQuery = window.matchMedia?.('(display-mode: window-controls-overlay)');

    const applyState = (isOverlayVisible: boolean, rect: DOMRect | null) => {
      const displayModeVisible = displayModeQuery?.matches ?? false;
      const rectVisible = rect ? rect.width > 0 && rect.height > 0 : false;
      setState({
        isVisible: isOverlayVisible || displayModeVisible || rectVisible,
        titlebarAreaRect: rect,
      });
    };

    const handleGeometryChange = (e: GeometryChangeEvent) => {
      const isOverlayVisible = e.visible ?? overlay?.visible ?? false;
      const titleBarRect = e.titlebarAreaRect ?? overlay?.getTitlebarAreaRect?.() ?? null;
      applyState(isOverlayVisible, titleBarRect);
    };

    const debouncedHandler = debounce(handleGeometryChange, DEBOUNCE_MS);

    // Initial state (no event yet)
    if (overlay) {
      const visible = overlay.visible ?? false;
      const rect = overlay.getTitlebarAreaRect?.() ?? null;
      applyState(visible, rect);
    }

    const handleDisplayModeChange = () => {
      const visible = overlay?.visible ?? false;
      const rect = overlay?.getTitlebarAreaRect?.() ?? null;
      applyState(visible, rect);
    };

    overlay?.addEventListener?.('geometrychange', debouncedHandler);
    displayModeQuery?.addEventListener?.('change', handleDisplayModeChange);

    return () => {
      debouncedHandler.cancel();
      overlay?.removeEventListener?.('geometrychange', debouncedHandler);
      displayModeQuery?.removeEventListener?.('change', handleDisplayModeChange);
    };
  }, []);

  return state;
}
